import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { absApi } from './api';
import { loadPool } from './absLink.js';
import { DRIVERS, EDITABLE_LINES, applyDrivers, fitDriversToEdits, impliedDriver, presetDrivers, quarterize } from './absFinancials.js';
import { downloadFinancialsXlsx } from './financialsExport.js';
import AroPanel from '../aro/AroPanel.jsx';

const AMBER = '#e8a317';
const k = (v) => (v == null ? null : v / 1e3);
const fmt = (v, d = 0) => (v == null || !Number.isFinite(v) ? null : v.toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d }));
const RANGES = { recent: 'Last 8Q + next 12Q', all: 'All', history: 'History only', forecast: 'Forecast only' };
const RATIONALE = {
  base: 'Approved DCA fits at the strip deck — the underwriting case.',
  upside: 'Volume × from the P10 of 12-month hindcast error (pool-level).',
  downside: 'Volume × from the P90 of 12-month hindcast error (pool-level).',
};

function Chart({ quarters, scenario }) {
  const W = 1100, H = 260, P = 40;
  const vals = quarters.flatMap((q) => [q.ncf, q.ncf_upside, q.ncf_downside, q.revenue].filter((v) => v != null).map(k));
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const bw = (W - P - 10) / Math.max(1, quarters.length);
  const y = (v) => H - 24 - ((v - min) / (max - min)) * (H - 40);
  const x = (i) => P + i * bw;
  const firstF = quarters.findIndex((q) => q.kind === 'forecast');
  const band = quarters.map((q, i) => (q.ncf_upside != null && q.ncf_downside != null ? [i, k(q.ncf_upside), k(q.ncf_downside)] : null)).filter(Boolean);
  const rev = quarters.map((q, i) => (q.revenue != null ? [x(i) + bw / 2, y(k(q.revenue)), q.kind] : null));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} data-testid="fin-chart" role="img" aria-label="NCF history and forecast">
      <defs>
        <pattern id="fin-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="#cbd5e1" /><line x1="0" y1="0" x2="0" y2="6" stroke="#94a3b8" strokeWidth="2" />
        </pattern>
      </defs>
      <line x1={P} y1={y(0)} x2={W - 10} y2={y(0)} stroke="#cbd5e1" />
      <text x="2" y="14" fontSize="10" fill="#64748b">${fmt(max)}k</text>
      {band.length > 1 && (
        <path d={`M${band.map(([i, u]) => `${x(i) + bw / 2},${y(u)}`).join('L')}L${band.slice().reverse().map(([i, , d]) => `${x(i) + bw / 2},${y(d)}`).join('L')}Z`} fill="#e2e8f0" opacity="0.8" />
      )}
      {quarters.map((q, i) => (q.ncf == null ? (
        <g key={q.quarter} />
      ) : (
        <rect key={q.quarter} x={x(i) + 2} width={Math.max(1, bw - 4)} y={Math.min(y(0), y(k(q.ncf)))} height={Math.abs(y(0) - y(k(q.ncf)))}
          fill={q.kind === 'actual' ? '#163a4f' : 'url(#fin-hatch)'} stroke={q.edited.length ? AMBER : 'none'}><title>{`${q.quarter} NCF $${fmt(k(q.ncf))}k (${q.kind})`}</title></rect>
      )))}
      <path d={rev.reduce((a, p, i) => (p ? `${a}${a && rev[i - 1] ? 'L' : 'M'}${p[0]},${p[1]}` : a), '')} fill="none" stroke={AMBER} strokeWidth="2" />
      {firstF > 0 && (
        <g>
          <line x1={x(firstF)} y1="8" x2={x(firstF)} y2={H - 20} stroke="#0c1f2e" strokeDasharray="4 3" />
          <text x={x(firstF) + 4} y="18" fontSize="10" fill="#0c1f2e" fontWeight="600">FORECAST · {scenario.toUpperCase()}</text>
        </g>
      )}
      {quarters.map((q, i) => (i % 4 === 0 ? <text key={`l${q.quarter}`} x={x(i)} y={H - 6} fontSize="9" fill="#64748b">{q.quarter}</text> : null))}
    </svg>
  );
}

const ROWS = [
  { key: 'grossMcf', label: 'Gross production (MMcf, pool)', v: (q) => (q.grossMcf != null ? fmt(q.grossMcf / 1e6, 2) : null), hist: true },
  { key: 'production', label: 'Net production (MMcfe)', v: (q) => (q.netMcfe != null ? fmt(q.netMcfe / 1e6, 3) : null), edit: 'production', raw: (q) => q.netMcfe / 1e6 },
  { key: 'price', label: 'Realized price ($/Mcfe)', v: (q) => fmt(q.realizedPrice, 2), edit: 'price', raw: (q) => q.realizedPrice },
  { key: 'revenue', label: 'Revenue ($k)', basisLabel: true, v: (q) => fmt(k(q.revenue)) },
  { key: 'hedge', label: 'Hedge settlements ($k)', v: (q) => fmt(k(q.hedge)) },
  { key: 'loe', label: 'LOE ($k)', v: (q) => fmt(k(q.loe)), edit: 'loe', raw: (q) => q.loe / 1e3 },
  { key: 'taxes', label: 'Production taxes ($k)', v: (q) => fmt(k(q.taxes)) },
  { key: 'capex', label: 'Capex ($k)', v: (q) => fmt(k(q.capex)), edit: 'capex', raw: (q) => q.capex / 1e3 },
  { key: 'ncf', label: 'NCF ($k)', v: (q) => fmt(k(q.ncf)), strong: true },
  { key: 'ncf_upside', label: 'NCF · Upside preset ($k)', v: (q) => fmt(k(q.ncf_upside)), fc: true },
  { key: 'ncf_downside', label: 'NCF · Downside preset ($k)', v: (q) => fmt(k(q.ncf_downside)), fc: true },
  { key: 'ds', label: 'ABS debt service, UW ($k)', v: (q) => fmt(k(q.absDebtService)), fc: true },
  { key: 'dscr', label: 'DSCR (x)', v: (q) => fmt(q.dscr, 2), fc: true },
  { key: 'parent', label: 'Cash to parent, base ($k)', v: (q) => fmt(k(q.absToParent)), fc: true },
  { key: 'notes', label: 'Notes outstanding ($k)', v: (q) => fmt(k(q.absNotes)), fc: true },
];

export default function Financials({ dealId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [scenario, setScenario] = useState('base');
  const [drivers, setDrivers] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [range, setRange] = useState('recent');
  const [aroRev, setAroRev] = useState(0); // bump → re-price after an ARO change
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setErr(null);
    const pool = loadPool(typeof window !== 'undefined' ? window.sessionStorage : null, dealId);
    absApi
      // ARO comes from the configured treatment (D2D estimate / overrides / seller-minimal / none).
      .linkFinancials({ dealId, pool: pool.length ? pool : undefined })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setScenario('base'); // reload resets drivers to base preset — keep the tab in sync
        setDrivers(presetDrivers(d.months, 1));
        setOverrides({});
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [dealId, aroRev]);

  const presets = useMemo(() => (data ? presetDrivers(data.months, data.scenarios?.[scenario] ?? 1) : null), [data, scenario]);
  const chooseScenario = useCallback((s) => {
    setScenario(s);
    setDrivers(presetDrivers(data.months, data.scenarios?.[s] ?? 1));
    setOverrides({});
  }, [data]);
  const months = useMemo(() => (data && drivers ? applyDrivers(data.months, drivers, overrides) : []), [data, drivers, overrides]);
  const allQ = useMemo(() => quarterize(months), [months]);
  const quarters = useMemo(() => {
    const f = allQ.findIndex((q) => q.kind === 'forecast');
    if (range === 'history') return allQ.filter((q) => q.kind !== 'forecast');
    if (range === 'forecast') return allQ.filter((q) => q.kind === 'forecast');
    if (range === 'recent' && f >= 0) return allQ.slice(Math.max(0, f - 8), f + 12);
    return allQ;
  }, [allQ, range]);

  if (err) {
    // API detail may be a JSON {code, message} string — show the message in plain words.
    let msg = err;
    let code = '';
    try {
      const j = JSON.parse(err);
      msg = j.message || err;
      code = j.code || '';
    } catch {
      /* plain text */
    }
    return (
      <div className="error-screen fin-empty" data-testid="fin-error">
        <h2>Financials unavailable</h2>
        <p>{msg.charAt(0).toUpperCase() + msg.slice(1)}.</p>
        {code === 'no_approved_forecasts' && (
          <a className="abs-run-btn fin-empty-cta" href="#assets?awtab=review">
            Open Asset workflow → Review &amp; approve
          </a>
        )}
      </div>
    );
  }
  if (!data || !drivers) return <div className="loading-screen"><div className="loading-spinner" /><p>Building history → forecast → ABS…</p></div>;

  const edits = Object.entries(overrides);
  const setCell = (line, q, raw) => {
    const v = Number(raw);
    setOverrides((o) => {
      const n = { ...o };
      if (raw === '' || !Number.isFinite(v)) delete n[`${line}|${q}`];
      else n[`${line}|${q}`] = v;
      return n;
    });
  };

  return (
    <div className="fin" data-testid="financials">
      {data.draftApprovals && (
        <div className="fin-draft-banner" data-testid="draft-banner" role="status">
          DRAFT · {data.draftApprovals.count} forecasts carry a provisional approval — “{data.draftApprovals.label}”. Not engineer-approved; never frozen.
        </div>
      )}
      {data.blocked && (
        <div className="abs-aro-banner" data-testid="fin-blocked" role="status">
          Underwriting blocked ({(data.blockers || []).join(', ')}) — forecast and ABS withheld (fail closed); history shown.
        </div>
      )}
      <section className="abs-section">
        <div className="fin-scen" data-testid="fin-scenarios">
          {['base', 'upside', 'downside'].map((s) => (
            <button key={s} type="button" className={`fin-scen-btn ${scenario === s ? 'active' : ''}`} onClick={() => chooseScenario(s)} data-testid={`fin-scen-${s}`}>
              {s[0].toUpperCase() + s.slice(1)} <span className="aw-small">{(data.scenarios?.[s] ?? 1).toFixed(2)}× vol</span>
            </button>
          ))}
        </div>
        <p className="aw-small">{RATIONALE[scenario]}</p>
      </section>
      <AroPanel dealId={dealId} onChange={() => setAroRev((n) => n + 1)} />

      <section className="abs-section">
        <h3>Financial history → forecast → ABS</h3>
        {data.basis?.label ? (
          <p className="aw-small" data-testid="fin-basis">{data.basis.label}</p>
        ) : null}
        <div className="fin-ranges">
          {Object.entries(RANGES).map(([kk, l]) => (
            <button key={kk} type="button" className={`fin-pill ${range === kk ? 'active' : ''}`} onClick={() => setRange(kk)} data-testid={`fin-range-${kk}`}>{l}</button>
          ))}
          <span className="aw-small fin-legend">
            <i className="sw sw-act" /> actual (LOS) <i className="sw sw-fc" /> forecast <i className="sw sw-band" /> downside–upside band <i className="sw sw-rev" /> revenue
          </span>
        </div>
        <Chart quarters={quarters} scenario={scenario} />
        <div className="abs-table-wrap fin-grid-wrap">
          <table className="abs-table fin-grid" data-testid="fin-grid">
            <thead>
              <tr>
                <th>$ / quarter</th>
                {quarters.map((q) => (
                  <th key={q.quarter} className={q.kind === 'actual' ? 'fin-act' : q.kind === 'gap' ? 'fin-gap' : 'fin-fc'}>
                    {q.quarter}<div className="fin-kind">{q.kindsLabel}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.key} className={r.strong ? 'abs-row-strong' : ''}>
                  <td>{r.basisLabel && data.basis?.revenueLabel ? data.basis.revenueLabel : r.label}{r.edit && <span className="aw-small"> ✎</span>}</td>
                  {quarters.map((q) => {
                    if (r.fc && q.kind !== 'forecast') return <td key={q.quarter} className="fin-na">—</td>;
                    const val = r.v(q);
                    const okey = `${r.edit}|${q.quarter}`;
                    const isEdited = r.edit && overrides[okey] != null;
                    if (r.edit && q.kind === 'forecast' && !q.mixed) {
                      const imp = isEdited ? impliedDriver(data.months, drivers, r.edit, q.quarter, overrides[okey]) : null;
                      return (
                        <td key={q.quarter} className={isEdited ? 'fin-edited' : 'fin-editable'} data-testid={`cell-${r.edit}-${q.quarter}`}>
                          {editing === okey ? (
                            <input autoFocus type="number" step="any" defaultValue={(r.raw(q) ?? 0).toFixed(3)} style={{ width: 80 }}
                              onBlur={(e) => { setCell(r.edit, q.quarter, e.target.value); setEditing(null); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(null); }} />
                          ) : (
                            <button type="button" className="fin-cell" onClick={() => setEditing(okey)}>{val ?? '—'}</button>
                          )}
                          {isEdited && (
                            <>
                              <button type="button" className="fin-reset" title="Reset to driver" onClick={() => setCell(r.edit, q.quarter, '')}>↺</button>
                              {imp && <div className="fin-implied">implies {imp.key === 'capexK' ? `$${imp.value.toFixed(0)}k/mo` : `${imp.value.toFixed(2)}×`}</div>}
                            </>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={q.quarter} className={val == null ? 'fin-missing' : q.kind === 'actual' ? 'fin-act' : ''} title={val == null ? `missing: ${(q.missing || []).join(', ')}` : undefined}>
                        {val ?? '–'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="aw-small fin-missing-note" data-testid="fin-missing-note">– = not in the source data for that period (e.g. capex and hedges are not in the seller LOS; $ history covers the 12 LOS months). Never estimated.</p>
        <ul className="aw-small fin-notes">{(data.notes || []).map((t) => <li key={t}>{t}</li>)}</ul>
      </section>

      <section className="abs-section fin-drivers" data-testid="fin-drivers">
        <h3>Drivers (forecast only)</h3>
        <div className="fin-slider-grid">
          {DRIVERS.map((d) => (
            <label key={d.key} className="fin-slider">
              <span className="fin-slider-head"><span>{d.label}</span><b>{d.fmt(drivers[d.key])}</b></span>
              <input type="range" min={d.min} max={d.max} step={d.step} value={drivers[d.key]} data-testid={`slider-${d.key}`}
                onChange={(e) => setDrivers({ ...drivers, [d.key]: Number(e.target.value) })} />
              <span className="aw-small">Preset · {d.fmt(presets[d.key])}</span>
            </label>
          ))}
        </div>
        <div className="fin-actions">
          <button type="button" className="abs-run-btn" disabled={!edits.length} onClick={() => setDrivers(fitDriversToEdits(data.months, drivers, overrides))} data-testid="fin-fit">
            Fit drivers to my edits ({edits.length})
          </button>{' '}
          <button type="button" className="aw-btn" onClick={() => { setDrivers(presets); setOverrides({}); }}>Reset to preset</button>{' '}
          <button type="button" className="aw-btn" data-testid="fin-export" onClick={() => downloadFinancialsXlsx(data, { drivers, presets, overrides, scenario })}>
            Export workbook (.xlsx)
          </button>
          <span className="aw-small"> Scenario tabs · monthly · drivers (preset vs edited) · sources &amp; provenance</span>
        </div>
      </section>
      <p className="aw-small" data-testid="fin-provenance">
        Approved case <code>{data.approvedCaseId}</code> · reviews: {data.reviewSource}
        {data.freeze ? ` · freeze #${data.freeze.freezeId} ${data.freeze.contentSha256.slice(0, 12)}` : ''} · runHash base <code>{data.runHashes?.base}</code>
        {' '}· historical $: {data.losSource || 'missing'}
      </p>
    </div>
  );
}
